import React from 'react';
import { Clickable as BaseClickable } from 'reakit';
import { CustomLinkComponentProps } from './types';

type BaseProps<S> = S & {
    children?: React.ReactNode;
    disabled?: boolean;
};

interface WithRoutingProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
    behaviour?: React.ComponentType<CustomLinkComponentProps>;
    href: string;
    openInNewTab?: boolean;
}

interface NoRoutingProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    behaviour?: never;
    href?: never;
    openInNewTab?: never;
}

export type ClickableWithRoutingProps<S> = BaseProps<S> & WithRoutingProps;
export type ClickableNoRoutingProps<S> = BaseProps<S> & NoRoutingProps;

export type ClickableProps<S> = ClickableWithRoutingProps<S> | ClickableNoRoutingProps<S>;
export type ClickableComponent<S> = React.ForwardRefExoticComponent<
    ClickableProps<S> & React.RefAttributes<HTMLButtonElement | HTMLAnchorElement>
>;

interface OpenInNewTabProps {
    target?: string;
    rel?: string;
}

export const Clickable = React.forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    ClickableProps<unknown>
>((props, ref) => {
    if ('href' in props && props.href !== undefined) {
        const { behaviour, openInNewTab, target, rel, ...anchorProps } = props;

        const getOpenInNewTabProps = (
            defaultValues: OpenInNewTabProps,
            openInNewTab?: boolean
        ): OpenInNewTabProps => {
            return openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : defaultValues;
        };

        const openInNewTabProps = getOpenInNewTabProps({ target, rel }, openInNewTab);

        if (!behaviour) {
            return (
                <BaseClickable
                    {...anchorProps}
                    {...openInNewTabProps}
                    as="a"
                    focusable
                    ref={ref as React.ForwardedRef<HTMLAnchorElement>}
                />
            );
        }

        const BehaviourComponent = behaviour;
        return <BehaviourComponent {...anchorProps} {...openInNewTabProps} focusable />;
    }

    const { type = 'button', ...buttonProps } = props;

    return (
        <BaseClickable
            {...buttonProps}
            type={type}
            focusable
            ref={ref as React.ForwardedRef<HTMLButtonElement>}
        />
    );
});
